import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';

import { SplashScreen } from '@/components/SplashScreen';
import i18n from '@/i18n/config';
import { LANGUAGE_STORAGE_KEY, type SupportedLanguage, DEFAULT_LANGUAGE } from '@/i18n/config';

type User = {
  id: string;
  email: string;
  name: string;
  role?: string;
  account_type?: string;
  isBlocked?: boolean;
  preferred_language?: SupportedLanguage;
  avatar_url?: string | null;
} | null;

// Helper para sincronizar idioma do usuário
function syncUserLanguage(userLanguage?: SupportedLanguage) {
  if (userLanguage && userLanguage !== i18n.language) {
    // Usuário tem preferência salva no backend - usar ela
    i18n.changeLanguage(userLanguage);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, userLanguage);
  }
}

type AuthContextType = {
  user: User;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<NonNullable<User>>) => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);


let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}


const getApiBase = () => {

  if (import.meta.env.VITE_API_URL) {
    return `${import.meta.env.VITE_API_URL}/auth`;
  }
  

  if (import.meta.env.PROD) {

    return '/auth';
  }
  

  return 'http://localhost:3000/auth';
};

const API_BASE = getApiBase();

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<number | null>(null);

  // Só mostra splash se há indício de sessão anterior
  const hadPreviousSession = localStorage.getItem('rememberMe') === 'true';


  const scheduleTokenRefresh = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Renova 2 minutos antes do token expirar (token dura 15 min)
    const refreshTime = 13 * 60 * 1000;

    refreshTimerRef.current = window.setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/refresh`, {
          method: 'POST',
          credentials: 'include',
        });

        if (res.ok) {
          const data = await res.json();
          accessToken = data.accessToken;
          setUser(data.user);
          scheduleTokenRefresh();
        } else {
          accessToken = null;
          setUser(null);
        }
      } catch (err) {
        console.error('Erro ao renovar token:', err);
        accessToken = null;
        setUser(null);
      }
    }, refreshTime);
  };


  useEffect(() => {
    const init = async () => {
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };


        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const res = await fetch(`${API_BASE}/session`, {
          credentials: 'include',
          headers,
        });

        if (!res.ok) {
          throw new Error('Sessão não encontrada');
        }

        const data = await res.json();

        if (data.user) {
          setUser(data.user);

          // Sincronizar idioma do usuário logado
          syncUserLanguage(data.user.preferred_language);

          if (data.accessToken) {
            accessToken = data.accessToken;
          }

          scheduleTokenRefresh();
        } else {
          setUser(null);
          accessToken = null;
        }
      } catch (err) {

        const isNetworkError = err instanceof TypeError &&
          (err.message.includes('fetch') || err.message.includes('Failed to fetch'));

        if (isNetworkError) {
          console.warn('Backend não disponível. Verifique se o servidor está rodando.');
        }

        setUser(null);
        accessToken = null;
      } finally {
        setLoading(false);
      }
    };

    init();


    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);


  const login = async (email: string, password: string, rememberMe: boolean = true) => {

    try {
      const res = await fetch(`${API_BASE}/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, rememberMe }),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Erro ao fazer login');
      }

      const data = await res.json();
      accessToken = data.accessToken;
      setUser(data.user);

      // Sincronizar idioma do usuário logado
      syncUserLanguage(data.user.preferred_language);

      scheduleTokenRefresh();

      if (rememberMe) localStorage.setItem('rememberMe', 'true');
      else localStorage.removeItem('rememberMe');
    } catch (err) {
      throw err;
    }
  };


  const register = async (email: string, password: string, name: string) => {

    try {
      const res = await fetch(`${API_BASE}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name }),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Erro ao registrar usuário');
      }

      const data = await res.json();
      accessToken = data.accessToken;
      setUser(data.user);


      scheduleTokenRefresh();

      localStorage.setItem('rememberMe', 'true');
    } catch (err) {
      throw err;
    }
  };


  const logout = async () => {
    try {
      await fetch(`${API_BASE}/signout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Erro no logout:', err);
    } finally {

      accessToken = null;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      localStorage.removeItem('rememberMe');
      setUser(null);
    }
  };

  // Atualiza dados do usuário localmente (após edição de perfil)
  const updateUser = (userData: Partial<NonNullable<User>>) => {
    setUser(prev => prev ? { ...prev, ...userData } : null);
  };

  // Recarrega dados do usuário do servidor
  const refreshUser = async () => {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const res = await fetch(`${API_BASE}/session`, {
        credentials: 'include',
        headers,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          setUser(data.user);
          if (data.accessToken) {
            accessToken = data.accessToken;
          }
        }
      }
    } catch (err) {
      console.error('Erro ao atualizar usuário:', err);
    }
  };

  // Só mostra splash screen se estava logado anteriormente (evita splash na tela de login)
  if (loading && hadPreviousSession) {
    return <SplashScreen visible subtitle="Validando sua sessão..." />;
  }

  // Se ainda está carregando mas não tinha sessão, não mostra nada (loading instantâneo)
  if (loading) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}


export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be usado dentro de um AuthProvider');
  return context;
}